import moment from "moment";

export const DEFAULT_RECORDED_DURATION = 20;
export const DEFAULT_RECORDED_TIME_UNIT = "seconds";

/**
 * Compute start/end timestamps for recorded playback around an alert.
 * Rule: split total duration in half; if odd, the extra second goes to POST-alarm.
 *
 * @param {string|Date|number} alertTime  ISO string, Date, or ms epoch (UTC-safe)
 * @param {number} totalSeconds           Total recorded duration (seconds)
 * @returns {{
 *   startISO: string,
 *   endISO: string,
 *   preSeconds: number,
 *   postSeconds: number
 * }}
 */
export function getRecordedWindow(alertTime, totalSeconds) {
	if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
		throw new Error("totalSeconds must be a positive number");
	}

	const alert =
		alertTime instanceof Date ? alertTime : new Date(typeof alertTime === "number" ? alertTime : String(alertTime));

	if (Number.isNaN(alert.getTime())) {
		throw new Error("alertTime is invalid");
	}

	const preSeconds = Math.floor(totalSeconds / 2);
	const postSeconds = totalSeconds - preSeconds;

	const start = new Date(alert.getTime() - preSeconds * 1000);
	const end = new Date(alert.getTime() + postSeconds * 1000);

	return {
		startISO: moment(start.toISOString()),
		endISO: moment(end.toISOString()),
		preSeconds,
		postSeconds
	};
}

export const toSeconds = (value, unit) => {
	switch (unit) {
		case "minutes":
			return value * 60;
		case "hours":
			return value * 3600;
		default:
			return value; // assume seconds
	}
};

export const normalizeRecordedDuration = (value, unit = DEFAULT_RECORDED_TIME_UNIT) => {
	const parsedValue = parseInt(value, 10);
	const normalizedUnit = unit || DEFAULT_RECORDED_TIME_UNIT;
	const seconds = toSeconds(parsedValue, normalizedUnit);

	if (!Number.isFinite(seconds) || seconds <= 0) {
		return {
			value: DEFAULT_RECORDED_DURATION,
			unit: DEFAULT_RECORDED_TIME_UNIT,
			seconds: DEFAULT_RECORDED_DURATION
		};
	}

	return {
		value: parsedValue,
		unit: normalizedUnit,
		seconds
	};
};
